<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog'
import { Edit, RotateCcw } from 'lucide-vue-next'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type { GlobalPromptSummary, GlobalPromptDetail, PromptPreview } from '@/types/Prompt'
import type { Skill } from '@/types/Skill'

const router = useRouter()
const store = useStore()
const member = computed(() => store.getters.getMember)

const prompts = ref<GlobalPromptSummary[]>([])
const loading = ref(false)

const dialogOpen = ref(false)
const editing = ref<GlobalPromptDetail | null>(null)
const draft = ref('')
const saving = ref(false)

const confirmResetDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingReset = ref<GlobalPromptSummary | null>(null)

// Preview state
const previewFlow = ref<'feature' | 'skill'>('feature')
const previewId = ref('')
const previewLoading = ref(false)
const preview = ref<PromptPreview | null>(null)

const skills = ref<Skill[]>([])
const selectedSkillId = ref<number | null>(null)

const previewLabel = computed(() =>
  previewFlow.value === 'feature' ? 'Feature UUID' : 'Project ID'
)

const setPreviewFlow = (flow: 'feature' | 'skill') => {
  previewFlow.value = flow
  preview.value = null
}

const fetchSkills = async () => {
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/skills` })
    skills.value = response.data
    if (!selectedSkillId.value && skills.value.length) {
      selectedSkillId.value = skills.value[0].id
    }
  } catch (error) {
    toast({ title: 'Error', description: 'Failed to fetch skills', variant: 'destructive' })
  }
}

const fetchPrompts = async () => {
  loading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/prompts` })
    prompts.value = response.data
  } catch (error) {
    toast({ title: 'Error', description: 'Failed to fetch prompts', variant: 'destructive' })
  } finally {
    loading.value = false
  }
}

const openEdit = async (prompt: GlobalPromptSummary) => {
  try {
    const response = await API({ method: 'GET', url: `${HOST}/admin/prompts/${prompt.key}` })
    editing.value = response.data
    draft.value = response.data.content
    dialogOpen.value = true
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to load prompt'
    toast({ title: message, variant: 'destructive' })
  }
}

const savePrompt = async () => {
  if (!editing.value) return
  if (!draft.value.trim()) {
    toast({ title: 'Prompt content cannot be empty', variant: 'destructive' })
    return
  }
  saving.value = true
  try {
    await API({
      method: 'PUT',
      url: `${HOST}/admin/prompts/${editing.value.key}`,
      data: { content: draft.value }
    })
    toast({ title: 'Prompt saved', description: 'Takes effect on the next request.' })
    dialogOpen.value = false
    await fetchPrompts()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to save prompt'
    toast({ title: message, variant: 'destructive' })
  } finally {
    saving.value = false
  }
}

const resetDraftToDefault = () => {
  if (editing.value) draft.value = editing.value.default
}

const promptReset = (prompt: GlobalPromptSummary) => {
  pendingReset.value = prompt
  confirmResetDialog.value?.open()
}

const resetPrompt = async () => {
  if (!pendingReset.value) return
  try {
    await API({ method: 'DELETE', url: `${HOST}/admin/prompts/${pendingReset.value.key}` })
    toast({ title: 'Reverted to default' })
    await fetchPrompts()
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to revert prompt'
    toast({ title: message, variant: 'destructive' })
  } finally {
    pendingReset.value = null
  }
}

const loadPreview = async () => {
  if (!previewId.value.trim()) {
    toast({ title: `${previewLabel.value} is required`, variant: 'destructive' })
    return
  }
  if (previewFlow.value === 'skill' && !selectedSkillId.value) {
    toast({ title: 'Select a skill', variant: 'destructive' })
    return
  }
  previewLoading.value = true
  preview.value = null
  try {
    const url = previewFlow.value === 'skill'
      ? `${HOST}/admin/prompts/preview/skill/${selectedSkillId.value}?project_id=${encodeURIComponent(previewId.value.trim())}`
      : `${HOST}/admin/prompts/preview/${previewFlow.value}/${encodeURIComponent(previewId.value.trim())}`
    const response = await API({ method: 'GET', url })
    preview.value = response.data
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to load preview'
    toast({ title: message, variant: 'destructive' })
  } finally {
    previewLoading.value = false
  }
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchPrompts()
  fetchSkills()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-6 flex-1 overflow-auto">
      <div>
        <h1 class="text-2xl font-bold">Prompts</h1>
        <p class="text-sm text-muted-foreground mt-1">
          Edit the base prompts sent to the LLM, and preview the exact assembled payload for any flow.
        </p>
      </div>

      <!-- Editable base prompts -->
      <div v-if="loading" class="p-4 space-y-3">
        <div v-for="i in 2" :key="i" class="h-12 bg-muted rounded animate-pulse"></div>
      </div>

      <div v-else class="border rounded-md">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Prompt</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Used in</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Source</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">~Tokens</th>
              <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in prompts" :key="p.key" class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle">
                <div class="font-medium">{{ p.label }}</div>
                <div class="text-sm text-muted-foreground">{{ p.description }}</div>
                <code class="text-xs text-muted-foreground">{{ p.key }}</code>
              </td>
              <td class="p-4 align-middle text-sm text-muted-foreground">{{ p.used_in }}</td>
              <td class="p-4 align-middle">
                <span
                  v-if="p.is_overridden"
                  class="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                >Edited</span>
                <span v-else class="text-xs text-muted-foreground">Default</span>
              </td>
              <td class="p-4 align-middle text-sm text-muted-foreground">{{ p.est_tokens.toLocaleString() }}</td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(p)" title="Edit prompt">
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    :disabled="!p.is_overridden"
                    @click="promptReset(p)"
                    title="Revert to default"
                  >
                    <RotateCcw class="h-4 w-4" :class="p.is_overridden ? 'text-destructive' : ''" />
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Preview the assembled payload -->
      <div class="border rounded-md p-4 space-y-4">
        <div>
          <h2 class="text-lg font-semibold">Preview assembled payload</h2>
          <p class="text-sm text-muted-foreground">
            See exactly what is sent to the provider for a specific flow.
          </p>
        </div>

        <div class="flex flex-wrap items-end gap-3">
          <div class="flex gap-1">
            <Button :variant="previewFlow === 'feature' ? 'default' : 'outline'" size="sm" @click="setPreviewFlow('feature')">Image Analyzer</Button>
            <Button :variant="previewFlow === 'skill' ? 'default' : 'outline'" size="sm" @click="setPreviewFlow('skill')">Skill</Button>
          </div>
          <div v-if="previewFlow === 'skill'" class="space-y-1 min-w-[200px]">
            <Label>Skill</Label>
            <select
              v-model="selectedSkillId"
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option v-for="s in skills" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="space-y-1 flex-1 min-w-[240px]">
            <Label>{{ previewLabel }}</Label>
            <Input v-model="previewId" :placeholder="previewLabel" @keyup.enter="loadPreview" />
          </div>
          <Button size="sm" :disabled="previewLoading" @click="loadPreview">
            {{ previewLoading ? 'Loading…' : 'Load preview' }}
          </Button>
        </div>

        <div v-if="preview" class="space-y-4">
          <p v-if="preview.note" class="text-xs text-muted-foreground italic">{{ preview.note }}</p>
          <div class="space-y-1">
            <Label>System</Label>
            <Textarea :model-value="preview.system" readonly rows="14" class="font-mono text-xs" />
          </div>
          <div v-if="preview.user_content" class="space-y-1">
            <Label>User content</Label>
            <Textarea :model-value="preview.user_content" readonly rows="8" class="font-mono text-xs" />
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:open="dialogOpen">
      <DialogContent class="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit: {{ editing?.label }}</DialogTitle>
          <DialogDescription>{{ editing?.description }}</DialogDescription>
        </DialogHeader>
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <Label>Prompt (markdown)</Label>
            <Button variant="ghost" size="sm" class="h-7 text-xs" @click="resetDraftToDefault">
              Load default
            </Button>
          </div>
          <Textarea v-model="draft" rows="20" class="font-mono text-xs" />
        </div>
        <DialogFooter>
          <Button variant="outline" @click="dialogOpen = false">Cancel</Button>
          <Button :disabled="saving" @click="savePrompt">{{ saving ? 'Saving…' : 'Save' }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DialogConfirm
      ref="confirmResetDialog"
      title="Revert to default?"
      description="This removes your override and restores the built-in prompt. This affects all flows using this prompt."
      @confirm="resetPrompt"
    />
  </div>
</template>
